import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Switch } from "@mui/material";
import React, { useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "react-toastify/dist/ReactToastify.css";
import instance from "../../redux/api";
import { numberFormatter } from "../../utils/generalFunctions";
import { CustomLoader } from "../CustomLoader";
import { CommonButton } from "../common/commonButton";
import DialogBox from "../common/dialog";
import AddNewAllocation from "./AddNewAllocation";
import AllocationsTable from "./AllocationsTable";
import CashAllocationEdit from "./CashAllocationEdit";
import CorrectiveTransfer from "./CorrectiveTransfer";
import CorrectionTypes from "./CorrectionTypes";
import FileUpload from "./FileUpload";
import "./cash-allocation.scss";
import { useAllocationList, useBankDetails, useSearchParams } from "./hooks";
import { parseDate } from "./utils";
import { commonToast } from "../common";

export default function DataTable() {
  const cashAllocationDetailsClose = "action-button filled pill danger";

  const { userData } = useSelector((state) => state?.user);

  const transactionId = useSearchParams("txn_id");
  const policyRef = useSearchParams("policy_ref");
  const [policyRefNo, setPolicyRefNo] = useState(policyRef);
  const [selectedRows, setSelectedRows] = useState([]);
  const [addingRecord, setAddingRecord] = useState(false);
  const [validationStatus, setValidationStatus] = useState([]);
  const [isEditingRecord, setIsEditingRecord] = useState(null);
  const { bankDetails, transactionDetails, isBankDetailsApiInProgress } = useBankDetails(transactionId);
  // const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [isAPIInProgress, setIsAPIInProgress] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(true);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);

  const [triggerCTWorkflow, setTriggerCTWorkflow] = useState(false);
  const [triggerCorrectionTypesWorkflow, setTriggerCorrectionTypesWorkflow] = useState(false);
  const [currentAllocation, setCurrentAllocation] = useState(null);
  const [cashAllocationDetailsEditOpen, setCashAllocationDetailsEditOpen] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});

  const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);

  const [openDialogueId, setOpenDialogueId] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [dialogueAllocation, setDialogueAllocation] = useState(null);

  // hooks
  const {
    allocationList,
    setAllocationList,
    saveAllocationData,
    deleteAllocationData,
    totalAllocatedAmount,
    totalReceivableAmount,
    loadData,
    apiProgressBar,
    setApiProgressBar,
    selectedRowIndex,
    setSelectedRowIndex,
    isEditPolicy,
    setIsEditPolicy
  } = useAllocationList(transactionId);

  useEffect(() => {
    // const allocationUserList = allocationList
    //   .map((item) => item?.policy_handler?.id)
    //   .filter((id) => id !== null && id !== undefined)
    if ((userData?.id && transactionDetails?.Assigned_User?.id && userData?.id === transactionDetails?.Assigned_User?.id)
      || !transactionDetails?.Assigned_User
      || userData?.role?.toLowerCase() === "manager"
      // || (userData?.id && allocationUserList.includes(userData?.id))
    ) {
      setIsAuthorizedUser(true);
    }
    else {
      setIsAuthorizedUser(false);
    }
  }, [userData, transactionDetails, allocationList]);

  const handleDeleteClick = async (id) => {
    try {
      setIsDeleteInProgress(true);
      const response = await deleteAllocationData(id);

      // close delete confirm dialog
      setOpenDeleteConfirmDialog(false);

      // Check if the deletion was successful
      if (response.status === 204 || response.status === 200) {
        // If successful, show a success toast
        commonToast("success", "Item deleted successfully");
        // If needed, update the state to reflect the deletion
        const updatedAllocations = allocationList.filter(
          (row) => row.id !== id
        );
        setAllocationList([...updatedAllocations]);
        setSelectedRows([]);
        setSelectedRowIndex(-1);
        setIsDeleteInProgress(false);
        if (allocationList.length > 0) {
          setPolicyRefNo(allocationList[0].id)
        }
      } else {
        // If deletion was not successful, show an error toast
        commonToast("error", `Error deleting item: ${response.statusText}`);
        setIsDeleteInProgress(false);
      }
    } catch (error) {
      // If an error occurs during deletion, show an error toast
      commonToast("error", `Error deleting item: ${error.message}`);
      setIsDeleteInProgress(false);
    }
  };

  const displayToastMessage = (message, type = "error") => {
    toast.error(message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 5000,
    });
  }

  // Trigger Auto Initiation of Corrective Transfer
  async function isAutoCorrectiveTransferTriggerd(allocationDetails) {
    try {
      let correctiveTransfers = null;
      try {
        // API Call to get corrective transfer details for allocation
        correctiveTransfers = await instance.get(
          `bankmanagement/get_corrective_by_allocation_id?cashallocation_id=${allocationDetails?.id}`
        );
      } catch (error) {
        if (error.status != 404) {
          commonToast("error", "Auto Intiation of corrective transfer failed.");

          return false;
        }
      }

      // Check if corrective Transfer already not exists for this allocation
      // Check if Producing Entity exists and not equal to receiving entity
      // Check if receivable amount is greater than 0
      if (
        allocationDetails?.allocation_status === "Allocated" &&
        (!correctiveTransfers || !correctiveTransfers?.data || correctiveTransfers?.data.length === 0) &&
        allocationDetails?.policy_fk?.Producing_Entity &&
        allocationDetails.policy_fk.Producing_Entity !== allocationDetails?.bank_txn?.bank_details?.entity_number &&
        allocationDetails?.receivable_amt != 0
      ) {
        setTriggerCTWorkflow(true);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      commonToast("error", "Auto Intiation of corrective transfer failed.");

      return false;
    }
  }

  function isAutoCorrectionTypesTriggered(allocationDetails) {
    try {
      const old_allocation_status = allocationDetails?.audit_fields?.fields?.filter(field => field.field_name === "allocation_status")[0].old_value ?? ""
      if (
        allocationDetails?.allocation_status.startsWith("Query")
        && allocationDetails?.allocation_status !== old_allocation_status
      ) {
        setTriggerCorrectionTypesWorkflow(true);
        return true;
      } else {
        return false;
      }
    }
    catch (error) {
      commonToast("error", "Auto Intiation of correction reason failed.");
      return false;
    }
  }

  const handleSubmit = async ({ policy, ...item }) => {
    try {
      // Bank roe should be 1 when Bank_Currency_Code and original_ccy is same
      if (item?.bank_txn?.Bank_Currency_Code === item?.original_ccy) {
        item.bank_roe = 1.000000;
      }
      if (Number(item?.bank_roe) <= 0) {
        displayToastMessage("Bank ROE cannot be Zero");
        return
      }

      // Add validation for SCM and Non-SCM binding agreement
      if (["SCM", "Non-SCM"].includes(item?.binding_agreement)) {
        const missingFields = [];
        if (!item?.XFIbatchno || item?.XFIbatchno === "0") missingFields.push("Batch");
        if (!item?.cash_reference) missingFields.push("Cash Reference");

        if (missingFields.length > 0) {
          displayToastMessage(`${missingFields.join(", ")} ${missingFields.length === 1 ? 'is' : 'are'} mandatory for ${item.binding_agreement} binding agreement`);
          return;
        }
      }

      // Trigger UI Notification when allocation_status is Allocated and Receivable, Allocated amounts are Zero
      if (item?.allocation_status === "Allocated") {
        if (Number(item?.receivable_amt) === 0) {
          displayToastMessage("Receivable amount cannot be Zero");
          return
        }
        if (Number(item?.allocated_amt) === 0) {
          displayToastMessage("Allocated amount cannot be Zero");
          return
        }
        if (parseFloat(item?.receivable_amt) != parseFloat(item?.allocated_amt)) {
          displayToastMessage("Remaining Balance should be Zero");
          return
        }
        
        // Added by Gaurav on 28-11-2024
        item.allocatedby = userData.user_name;
        item.allocation_date = parseDate(new Date());
      }

      setCurrentAllocation(item);

      // Update audit fields
      const { audit_fields: auditFields } = item;
      auditFields.fields[0].value = item?.policy_id;
      auditFields.fields[1].value = item?.allocation_status;
      auditFields.fields[2].value = item?.receivable_amt;
      auditFields.fields[3].value = item?.allocated_amt;
      auditFields.user_id = userData?.id;

      const payload = {
        ...item,
        bank_txn: item?.bank_txn?.id ?? 0,
        policy_fk: item?.policy_fk?.id ?? null,
        policy_pk: item?.policy_pk ?? item?.policy_fk?.id,
        policy_mf: item?.policy_mf ?? [],
        audit_fields: auditFields,
        updatedFields: updatedFields[selectedRowIndex] || {}
      };
      setIsAPIInProgress(true);
      const response = await saveAllocationData(payload);
      setIsAPIInProgress(false);

      if (response.status === 200 || response.status === 201) {
        let shouldReload = true;

        // Auto Initiation of Corrective transfer check
        const isAutoInitiationTriggered = await isAutoCorrectiveTransferTriggerd(item);
        if (isAutoInitiationTriggered) {
          shouldReload = false;
        }

        // Auto Initiation of Correction Type check
        const isCorrectionTypesAutoInitiationTriggered = isAutoCorrectionTypesTriggered(item);

        if (isCorrectionTypesAutoInitiationTriggered) {
          shouldReload = false;
        }

        commonToast("success", "Item Updated successfully!");

        if (shouldReload) {
          await loadData();
          // Clear the updated fields for this row after save
          setUpdatedFields(prev => {
            const newState = { ...prev };
            delete newState[selectedRowIndex];
            return newState;
          });
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setIsAPIInProgress(false);
      commonToast("error", err?.data?.msg || "Unable to save cash allocation data");
    }
  };

  const startAddingNewRow = () => {
    if(isAuthorizedUser){
      setAddingRecord(true);
    }
    else{
      toast.error("You are not assigned user!! Please re-assign and then try.", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
    } 
  };

  const [isAddRow, setIsAddRow] = useState(false)

  const handleAddRow = (data) => {
    commonToast("success", "Successfully Added new allocation!");
    setPolicyRefNo(data?.id);
    setIsAddRow(true);
    if (data?.bank_txn) {
      loadData(data?.policy_id);
    } else {
      try {
        instance
          .get(
            `bankmanagement/get_transactions_by_txn_id/?txn_id=${encodeURIComponent(data?.bank_txn)}`
          )
          .then((response) => {
            let data = response.data;
            // disable the row then when the allocation status is "Allocated"
            data = data?.map((item) => {
              return {
                ...item,
                disabled: item?.allocation_status === "Allocated",
                audit_fields:{
                  user_id: userData?.id,
                  fields: [
                    {
                      field_name: "policy_id",
                      old_value: item?.policy_id,
                      value: item?.policy_id
                    },
                    {
                      field_name: "allocation_status",
                      old_value: item?.allocation_status,
                      value: item?.allocation_status
                    },
                    {
                      field_name: "receivable_amt",
                      old_value: item?.receivable_amt,
                      value: item?.receivable_amt
                    },
                    {
                      field_name: "allocated_amt",
                      old_value: item?.allocated_amt,
                      value: item?.allocated_amt
                    }
                  ],
                  txn_id: item?.audit_txn?.id,
                  audit_txn_name: "CASH_ALLOCATION"
                }
              }
            });
            setAllocationList([...data]);
          })
          .catch((error) => {
            console.error("Error fetching allocations:", error);
            commonToast("error", "Unable fetch the added new allocation!");
          });
      } catch (err) {
        console.error("err", err);
      }
    }

    cancelAddRow();
  };

  const cancelAddRow = () => {
    setAddingRecord(false);
    setIsEditingRecord(null);
  };

  // Add state for modal visibility
  const [isPolicyErrorModalOpen, setIsPolicyErrorModalOpen] = useState(false);

  // Function to handle modal close
  const handleClosePolicyErrorModal = () => {
    setIsPolicyErrorModalOpen(false);
  };

  const handleDeleteRow = async () => {
    if (selectedRowIndex === -1) {
      commonToast("error", `Please select one row to delete`);
      return;
    }

    if (allocationList[selectedRowIndex]?.is_contra_allocation) {
      commonToast("error", "Contra allocation cannot be deleted.");

      return;
    }

    if (allocationList[selectedRowIndex]?.child_contra_id > 0) {
      commonToast("error", "Can't delete allocation which has contra allocation");

      return;
    }

    try {
      const response = await instance.get(`bankmanagement/cash_allocation_activities/${allocationList[selectedRowIndex].id}/`);
      if (response.status === 200 && response.data.message === "Success") {
        setOpenDeleteConfirmDialog(true);
      } else {
        //setErrorText(response.data.data)
        setOpenDeleteConfirmDialog(true);
        // setIsPolicyErrorModalOpen(true);
      }
    } catch (error) {
      commonToast("error", 'Error while getting details of the policy');
    }
  };

  const handleEditRow = () => {

    if (selectedRowIndex === -1) {
      commonToast("error", 'Error while getting details of the policy');
      return;
    }

    if (allocationList[selectedRowIndex]?.is_contra_allocation) {
      commonToast("error", "Contra allocation cannot be edited.");

      return;
    }

    if (allocationList[selectedRowIndex]?.child_contra_id > 0) {
      commonToast("error", "Can't edit allocation which has contra allocation");

      return;
    }

    setCashAllocationDetailsEditOpen(true);
  }

  const toggleTransactionDetails = useCallback(() => {
    setShowTransactionDetails((t) => !t);
  }, [setShowTransactionDetails]);

  const handleDeleteCancelled = () => {
    setOpenDeleteConfirmDialog(false);
  };

  const handleDeleteConfirmed = () => {
    const row = allocationList[selectedRowIndex];
    handleDeleteClick(row.id);
  };

  return (
    <>
      <div
        className={`cash-allocation-page ${
          showTransactionDetails ? "with-details" : "without-details"
        }`}
      >
        <div className="top-details-section">
          <div className="header">
            <div className="page-title-container">
              <span className="page-title">Cash Allocation Details</span>
            </div>
            <div className="page-actions" style={{ display: "flex" }}>
              <div>
                {" "}
                <div style={{ marginRight:"10px" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div className="cash-alloc-indicator historical"
                    style={{ marginRight: "10px" }} />{" "}
                    Historical
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div className="cash-alloc-indicator non-historical"
                      style={{ marginRight: "10px" }}
                    />{" "}
                    Non-Historical
                  </div>
                </div>
              </div>
              <CommonButton
                text="X Close Details"
                handleClick={() => window.history.back()}
                className={cashAllocationDetailsClose}
              />
            </div>
          </div>
          <div className="transaction-details">
            <div className="transaction-details-header"> 
              <span className="switch-container">
                <span className="page-title">View Bank Details</span>
                <Switch
                  inputProps={{ "aria-label": "Show Transaction Details" }}
                  onChange={toggleTransactionDetails}
                  checked={showTransactionDetails}
                />
                <span className="top-amount-details">
                  Total Bank Rcvd:{" "}
                  <span>
                    {numberFormatter(transactionDetails.Receivable_Amount)}
                  </span>
                </span>
                <span className="top-amount-details">
                  Policy Prem:{" "}
                  <span>
                    {numberFormatter(totalReceivableAmount)}
                  </span>
                </span>
                <span className="top-amount-details">
                  Alloc:{" "}
                  <span>{numberFormatter(totalAllocatedAmount)}</span>
                </span>
                <span className="top-amount-details">
                  Bank Rcvd vs Prem Rcvd:{" "}
                  <span>
                    {numberFormatter(transactionDetails.Receivable_Amount - totalReceivableAmount)}
                  </span>
                </span>
                <span className="top-amount-details">
                  Policy vs Alloc:{" "}
                  <span>
                    {numberFormatter(totalReceivableAmount - totalAllocatedAmount)}
                  </span>
                </span>
                <span className="top-amount-details">
                  Total Unalloc:{" "}
                  <span>
                    {numberFormatter(transactionDetails.Receivable_Amount - totalAllocatedAmount)}
                  </span>
                </span>
              </span>
              <FileUpload transaction={transactionDetails}/>
            </div>
            {showTransactionDetails && (
              <div className="transaction-details-card">
                {bankDetails.map(({ label, value }) => (
                  <span
                    key={label}
                    className={`transaction-item ${
                      label === "Payment reference" ? "w-50" : ""
                    }`}
                  >
                    <span className="transaction-item-label">{label}</span>
                    <span className="transaction-item-value" title={value}>
                      : {value}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="action-container">
            <h4 className="">Remittance List</h4>
            <div className="table-actions">
              <CommonButton
                text="Add"
                handleClick={() => startAddingNewRow()}
                sx={{ borderRadius: "30px" }}
                hoverColor="#FF5A01"
                disabled={apiProgressBar || isAPIInProgress || isBankDetailsApiInProgress}
              />
              <CommonButton
                text="Edit"
                handleClick={() => handleEditRow()}
                sx={{ borderRadius: 100 }}
                variant="outlined"
                disabled={
                  selectedRowIndex === -1 ||
                  userData?.role?.toLowerCase() !== "manager" ||
                  apiProgressBar ||
                  isAPIInProgress ||
                  isBankDetailsApiInProgress
                }
                hoverColor="#FF5A01"
              />
              {allocationList?.length > 0 && (
                <CommonButton
                  text="Delete"
                  handleClick={() => handleDeleteRow()}
                  sx={{ borderRadius: "30px" }}
                  hoverColor="#FF5A01"
                  disabled={userData?.role?.toLowerCase() !== "manager" || apiProgressBar || isAPIInProgress || isBankDetailsApiInProgress}
                />
              )
              }
            </div>
          </div>
        </div>

        <div className="table-section">
          <AllocationsTable
            allocations={allocationList}
            setAllocations={setAllocationList}
            validationStatus={validationStatus}
            setValidationStatus={setValidationStatus}
            handleSave={handleSubmit}
            transaction={transactionDetails}
            selectedRowIndex={selectedRowIndex}
            setSelectedRowIndex={setSelectedRowIndex}
            apiProgressBar={apiProgressBar}
            setApiProgressBar={setApiProgressBar}
            setIsEditingRecord={setIsEditingRecord}
            policyRef={policyRef}
            updatedFields={updatedFields}
            setUpdatedFields={setUpdatedFields}
            isAuthorizedUser={isAuthorizedUser}
            openDialogueId={openDialogueId}
            setOpenDialogueId={setOpenDialogueId}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            menuId={menuId}
            setMenuId={setMenuId}
            dialogueAllocation={dialogueAllocation}
            setDialogueAllocation={setDialogueAllocation}
            policyRefNo={policyRefNo}
            setPolicyRefNo={setPolicyRefNo}
            isEditPolicy={isEditPolicy}
            setIsEditPolicy={setIsEditPolicy}
            isAddRow={isAddRow}
            setIsAddRow={setIsAddRow}
          />
        </div>

        {isAPIInProgress && <CustomLoader />}

        <ToastContainer />
        
        {(addingRecord || isEditingRecord) && (
          <AddNewAllocation
            onSuccess={handleAddRow}
            onCancel={cancelAddRow}
            isEditingRecord={isEditingRecord}
            transaction={transactionDetails}
          />
        )}

        <DialogBox
          title="Delete Confirmation"
          body={`Are you sure you want to delete this policy ${allocationList?.[selectedRowIndex]?.policy_id}?`}
          confirmText="Delete"
          isOpen={openDeleteConfirmDialog}
          onCancel={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
          isDisableConfirm={isDeleteInProgress}
        />

        {/* Auto Initiation Of Corective Transfer */}
        {triggerCTWorkflow && (
          <CorrectiveTransfer
            allocation={allocationList[selectedRowIndex]}
            handleClose={() => {
              // Logic to close the dialog when the handleClose function is triggered
              setTriggerCTWorkflow(false);  // Or any method to control dialog visibility
            }}
            transaction={transactionDetails}
            currencies={[]}
            isAutoInitiation={true}
            isAuthorizedUser={isAuthorizedUser}
          />
        )}

        {/* Auto Initiation Of Correction Types */}
        {triggerCorrectionTypesWorkflow && (
          <CorrectionTypes
            allocation={allocationList[selectedRowIndex]}
            handleClose={() => {
              setTriggerCorrectionTypesWorkflow(false);
            }}
            transaction={transactionDetails}
            currencies={[]}
            isAutoInitiation={true}
            isAuthorizedUser={isAuthorizedUser}
          />
        )}

      </div>
      {/* Add the Dialog component for the policy error modal */}
      <Dialog
        open={isPolicyErrorModalOpen}
        onClose={handleClosePolicyErrorModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Warning!"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Policy cannot be updated as there are activities associated to it.
          </DialogContentText>
          <DialogContentText id="alert-dialog-description" sx={{ mt: 2 }}>
            Kindly check !!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePolicyErrorModal} color="primary" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <CashAllocationEdit loadData={loadData} policy={allocationList?.[selectedRowIndex]} isOpen={cashAllocationDetailsEditOpen} onCancel={() => setCashAllocationDetailsEditOpen(false)} />
    </>
  );
}

// const TriggerCorrectiveTransferWorkflow = ({ currentAllocation, transactionDetails, loadData }) => {
//   const { formData, setFormData, submit } = useCorrectiveTrf(currentAllocation, transactionDetails);

//   const triggerCTWorkflowAPI = async () => {
//     setFormData({
//       ...formData,
//       bank_curr: currentAllocation?.policy_fk?.Settlement_Ccy ?? null,
//     })
//     await submit();
//     loadData();
//   }

//   useEffect(() => {
//     triggerCTWorkflowAPI();
//   }, []);

//   return (
//     <>
//     </>
//   )
// };
